import { HttpClient } from '@angular/common/http';
import { Injectable, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Auth } from '../models/auth';

export const AUTH_ME_URL = '/api/auth/me';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly baseUrl = AUTH_ME_URL;

  public readonly isAuthenticated = signal<boolean>(false);

  public readonly userInfo = signal<Auth | null>(null);

  public readonly resellerEmail = computed(() => this.userInfo()?.resellerEmail || '');

  public readonly isSuperAdmin = computed(() => !!this.userInfo()?.superAdminEmail);

  public readonly authChecked = signal(false);

  private authCheck$: Observable<boolean> | null = null;

  private returnUrl: string | null = null;

  constructor() {
    afterNextRender(() => this.checkAuth().subscribe());

    effect(() => {
      if (!this.isAuthenticated()) {
        return;
      }

      const returnUrl = this.consumeReturnUrl();
      if (returnUrl) {
        this.router.navigateByUrl(returnUrl);
      }
    });
  }

  public rememberReturnUrl(url: string): void {
    this.returnUrl = url;
  }

  private consumeReturnUrl(): string | null {
    const url = this.returnUrl;

    this.returnUrl = null;

    return url;
  }

  public checkAuthValue(proposedValue: boolean): void {
    if (proposedValue !== this.isAuthenticated()) {
      this.checkAuth(true).subscribe();
    }
  }

  public checkAuth(force = false): Observable<boolean> {
    if (force || !this.authCheck$) {
      this.authCheck$ = this.http.get<Auth>(this.baseUrl).pipe(
        tap((userInfo) => {
          this.isAuthenticated.set(true);
          this.userInfo.set(userInfo);
        }),
        map(() => true),
        catchError(() => {
          this.isAuthenticated.set(false);
          this.userInfo.set(null);
          return of(false);
        }),
        tap(() => this.authChecked.set(true)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.authCheck$;
  }

  public invalidate(): void {
    this.isAuthenticated.set(false);
    this.userInfo.set(null);
    this.authCheck$ = null;
  }

  public logoutUrlTree(router: Router, ref?: string): UrlTree {
    return router.createUrlTree(['/AP/PartnerLogin.aspx'], { queryParams: { logoff: 1, ref: ref || router.url } });
  }
}
